import React, { useCallback, useEffect, useMemo, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import VisibilitySensor from "react-visibility-sensor";
import { debounce, getPageSlotHeight } from "../../utils";
import { CircularLoader, Resizer } from "..";
import { translate } from "../../providers";
import styles from "./PdfViewer.module.css";
import { setMyReviewRightPanel } from "../../../views/my-review/slice/project-document/project-document-slice";
import { useMyReviewRightPanelWidth } from "../../hooks";
import { getRightPanelMinVw, RIGHT_PANEL_MAX_VW } from "../../hooks/use-review-right-panel-width";

const debounceFetchPages = debounce((handleGetPages, start, end) => {
	handleGetPages(start, end);
}, 500);

export default function PdfViewer({
	className = "",
	content = [],
	height,
	highlightPage = false,
	inPreview = false,
	numberOfPages = 0,
	onGetMetadata = null,
	onGetPage = null,
	onRenderLayers = null,
	onSetPage = null,
	pageDimensions = [],
	scrollRef = null,
	showScrollbar = true,
	zoom = 100,
	onScroll = null,
	onMouseEnter = null,
	onRenderAdditionalPanel = undefined,
	onRenderGlobalOverlay = null,
	width,
}) {
	const rightPanelWidth = useMyReviewRightPanelWidth();
	const leftPanelWidth = useSelector(({ srProjectDocument }) => srProjectDocument.myReviewLeftPanelWidth);
	const openDrawer = useSelector(({ srProjectDocument }) => srProjectDocument.openMultiToc);
	const [history, setHistory] = useState([]);
	const dispatch = useDispatch();
	useEffect(() => {
		if (content.length === 0) {
			setHistory([]);
		}
	}, [content]);
	const handleResize = (newWidth) => {
		dispatch(setMyReviewRightPanel(newWidth));
	};
	const handleGetPages = useCallback(
		(start, end) => {
			if (typeof onGetPage === "function") {
				batch(() => {
					for (let i = start; i <= end; i++) {
						if (i > -1 && i < numberOfPages) {
							if (!history.some((c) => c === i)) {
								setHistory((prev) => [...prev, i]);
								onGetPage(i + 1);
							}
							if (typeof onGetMetadata === "function") {
								onGetMetadata(i + 1);
							}
						}
					}
				});
			}
		},
		[history, onGetMetadata, onGetPage, numberOfPages]
	);

	const handleVisibility = (isVisible, id) => {
		if (isVisible && typeof onSetPage === "function") {
			onSetPage(id);
			debounceFetchPages(handleGetPages, id - 5, id + 5);
		}
	};
	const handleTransform = (dimensions, effectiveHeigth, effectiveWidth) => {
		if (dimensions?.rotation === 90) {
			return `rotate(90deg) translateY(${effectiveHeigth * -1}px)`;
		}
		if (dimensions?.rotation === 180) {
			return `rotate(180deg) translateY(${effectiveHeigth * -1}px) translateX(${effectiveWidth * -1}px)`;
		}
		if (dimensions?.rotation === 270) {
			return `rotate(270deg) translateX(${effectiveWidth * -1}px)`;
		}
		return "";
	};

	const rowRenderer = ({ key, index, dimensions }) => {
		const row = content.find((c) => c.page === index + 1);
		const effectiveWidth = (dimensions.width * zoom) / 100 || 0;
		const effectiveHeigth = (dimensions.height * zoom) / 100 || 0;
		const { image, isLoading, ...metadata } = row || {};
		const pdfContainerId = document.getElementById("pdfContainer_id");
		const preventDragHandler = (e) => {
			e.preventDefault();
		};
		return (
			<div
				key={key}
				style={{
					height: (getPageSlotHeight(dimensions) * zoom) / 100 || 0,
					width:
						dimensions?.rotation === 90 || dimensions?.rotation === 270 ? effectiveHeigth : effectiveWidth,
				}}
			>
				<div
					className={styles.row}
					data-testid="pdf-reviewer.row"
					id={`row_${key + 1}_pdf_viewer`}
					style={{
						height:
							dimensions?.rotation === 90 || dimensions?.rotation === 270 ? undefined : effectiveHeigth,
						width: dimensions?.rotation === 90 || dimensions?.rotation === 270 ? effectiveWidth : undefined,
						transform: handleTransform(dimensions, effectiveHeigth, effectiveWidth),
						transformOrigin: "top left",
					}}
				>
					<VisibilitySensor
						delayedCall
						partialVisibility
						containment={pdfContainerId}
						offset={{ bottom: height * 0.7, top: height * 0.3 }}
						onChange={(isVisible) => {
							if (handleVisibility) {
								handleVisibility(isVisible, index);
							}
						}}
					>
						<div
							className={styles.imageContainer}
							data-highlight={highlightPage}
							onDragStart={preventDragHandler}
						>
							{row &&
								((isLoading && (
									<div className={styles.image__loader}>
										<CircularLoader color="primary" size={80} />
									</div>
								)) ||
									(image && (
										<>
											<img
												alt={`image_${key}`}
												className={styles.image}
												height={effectiveHeigth}
												role="presentation"
												src={image}
												width={effectiveWidth}
											/>
											{typeof onRenderLayers === "function" && (
												<>
													{onRenderLayers({
														metadata,
														height: effectiveHeigth,
														width: effectiveWidth,
														tableHeight: height,
														onChangeVisibility: null,
														openedQuickAccess: false,
													})}
												</>
											)}
										</>
									)) || (
										<div className={styles.image__error}>
											{translate("common:pdf-image.unavailable", { page: index + 1 })}
										</div>
									))}
						</div>
					</VisibilitySensor>
				</div>
			</div>
		);
	};

	const totalHeight = useMemo(
		() => ((pageDimensions || []).reduce((acc, curr) => acc + getPageSlotHeight(curr), 0) * zoom) / 100 || 0,
		[pageDimensions, zoom]
	);
	const additionalPanel = useMemo(() => {
		if (onRenderAdditionalPanel && typeof onRenderAdditionalPanel === "function") {
			return onRenderAdditionalPanel();
		}
		return null;
	}, [onRenderAdditionalPanel]);
	return (
		<>
			<div
				ref={scrollRef}
				className={`${styles.pdfViewer} ${!showScrollbar && styles.pdfViewer__hideScroll} ${className}`}
				data-has-right-panel={onRenderAdditionalPanel !== undefined}
				data-right-panel-on={!!additionalPanel}
				id="pdfContainer_id"
				style={{ height }}
				onMouseEnter={onMouseEnter}
				onScroll={onScroll}
			>
				<div className={styles.pdfViewerList} style={{ height: totalHeight }}>
					<div
						className={styles.pdfViewerList__container}
						data-has-right-panel={onRenderAdditionalPanel !== undefined}
						data-right-panel-on={!!additionalPanel}
						id="pdfViewerList_id"
						style={
							inPreview
								? { width: "100%" }
								: {
										width:
											width ||
											`calc(${100 - (openDrawer ? leftPanelWidth : 0) - rightPanelWidth}vw  - ${
												openDrawer ? 0 : 47
											}px + 91px)`,
								  }
						}
					>
						{pageDimensions.map((dimensions, index) => rowRenderer({ key: index, index, dimensions }))}
						{typeof onRenderGlobalOverlay === "function" && onRenderGlobalOverlay()}
					</div>
					{!!additionalPanel && (
						<>
							<Resizer
								isGlobalWidth
								isOnLeft
								additionalPixels={-180}
								className={styles.resizer}
								idAddition="right"
								max={RIGHT_PANEL_MAX_VW}
								min={getRightPanelMinVw()}
								onResize={handleResize}
							/>
							<div className={styles.rightPanel} style={{ width: `calc(${rightPanelWidth}vw - 180px)` }}>
								{additionalPanel}
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
}
